<script setup lang="ts">
  import type { ProductVariantPickerResource, ProductVariantSimpleResource } from '@storefront/resources';
  import { ref, onMounted } from 'vue';
  import { addToCartEvent, useGoogleTagManager } from '@storefront/storefront';
  import { useShared } from '@othercommerce/foundation';

  interface Props {
    variant: ProductVariantPickerResource,
    requestedAmount: number,
    related: ProductVariantSimpleResource[],
  }

  const props = defineProps<Props>();

  const shared = useShared();
  const gtm = useGoogleTagManager();

  const parameters = ref({
    navigation: true,
    breakpoints: {
      1: {
        slidesPerView: 1.5,
        spaceBetween: 10,
      },
      576: {
        slidesPerView: 2,
        spaceBetween: 10,
      },
      767: {
        slidesPerView: 3,
        spaceBetween: 10,
      },
      992: {
        slidesPerView: 4,
        spaceBetween: 10,
      },
    },
  });

  onMounted(() => {
    gtm.push(addToCartEvent(props.variant, props.requestedAmount, shared.value.activeCurrencyCode));
  });
</script>
<template>
  <UiModal visitable open large class="cart-button-success-modal" id="cart-success-modal" aria-labelledby="cart-success-modal-title">
    <SfHeading type="h2" class="cart-button-success-modal__heading" id="cart-success-modal-title">
      <SfIconSuccess aria-hidden="true"/>
      {{ $t('SHOP.CART.HEADER.PRODUCT_ADDED_TO_CART') }}
    </SfHeading>
    <section class="cart-button-success-modal__container">
      <div class="cart-button-success-modal__image">
        <MediaPicture :photo="variant.photo" sizes="300x300 2x min 577, 150x150 min 577, 150x150 2x, 75x75" />
      </div>
      <div class="cart-button-success-modal__picker-col">
        <SfHeading class="cart-button-success-modal__product-title" type="h3">{{ variant.title }}</SfHeading>
        <SfStars v-if="variant.reviews.count > 0" :rating="variant.reviews.average" :count="variant.reviews.count" />
        <SfProductCatalogPrice :item="variant" />
        <SfProductPrice :price="variant" />
        <SfProductOmnibus :price="variant" />
      </div>
    </section>
    <section v-if="related.length > 0" class="cart-button-success-modal__related">
      <header>
        <SfHeading type="h3" class="cart-button-success-modal__related-header">
          {{ $t('SHOP.CART.HEADER.YOU_MIGHT_ALSO_LIKE') }}
        </SfHeading>
      </header>
      <SfProductsCarousel :products="related" :parameters />
    </section>
    <UiButtonGroup class="cart-button-success-modal__buttons" end>
      <SfButton back preserveScroll>
        {{ $t('SHOP.CART.ACTION.CONTINUE_SHOPPING') }}
      </SfButton>
      <SfButton :href="$route('web.cart.show')" primary>
        {{ $t('SHOP.CART.ACTION.GO_TO_CART') }}
      </SfButton>
    </UiButtonGroup>
  </UiModal>

  <RouterView />
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .cart-button-success-modal {

    &__heading {
      display: inline-flex;
      gap: 0.75rem;
      height: 130%;

      svg {
        color: var(--sf-status-color-success, #1d8106);
        margin-top: 0.125rem;
      }
    }

    &__container {
      display: grid;
      grid-template-columns: 150px 1fr;
      gap: 1rem;
    }

    &__product-title {
      font-size: 1rem;
    }

    &__picker-col,
    &__related {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    @include media.query(sm) {
      &__heading {
        gap: 0.5rem;

        svg {
          margin-top: 0.25rem;
        }

      }
      &__container {
        grid-template-columns: 75px 1fr;
      }

      &__buttons {
        flex-direction: column;
        align-items: stretch;
        flex-wrap: nowrap;
      }
    }
  }
</style>
